import { BUSINESS_SCOPE_KINDS, type BusinessScopeKind } from "./enums";
import { parseOneOf, type OneOfParseError } from "./guards";
import { err, ok, type Result } from "./result";
import { cleanNullableText, cleanRequiredText, type NullableText } from "./text";

export type BusinessScopeError =
  | { readonly kind: "emptyScopeName" }
  | { readonly kind: "selfParentScope" }
  | { readonly kind: "descendantParentScope" };

export const businessScopeErrorMessage = (error: BusinessScopeError): string => {
  switch (error.kind) {
    case "emptyScopeName":
      return "Area name is required";
    case "selfParentScope":
      return "An area cannot be its own parent";
    case "descendantParentScope":
      return "An area cannot be nested under one of its own children";
  }
};

export const BUSINESS_SCOPE_KIND_OPTIONS = [
  "mine",
  "site",
  "businessUnit",
  "subsidiary",
  "projectArea",
  "other",
] as const satisfies readonly BusinessScopeKind[];

export const businessScopeKindDisplayName = (kind: BusinessScopeKind): string => {
  const labels: Record<BusinessScopeKind, string> = {
    mine: "Mine",
    site: "Site",
    businessUnit: "Business unit",
    subsidiary: "Subsidiary",
    projectArea: "Project area",
    other: "Other",
  };
  return labels[kind];
};

export const businessScopeKindInputResult = (
  value: string,
): Result<BusinessScopeKind, OneOfParseError> => parseOneOf(BUSINESS_SCOPE_KINDS, value);

export const businessScopeKindFromInput = (
  value: string,
  fallback: BusinessScopeKind,
): BusinessScopeKind => {
  const parsed = businessScopeKindInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export type BusinessScopeDisplay = {
  readonly name: string;
};

export const businessScopeDisplayName = (
  scope: BusinessScopeDisplay | null | undefined,
): string => scope?.name ?? "Whole company";

export const businessScopeBadgeToneClass = (): string =>
  "bg-cyan-50 text-cyan-700 ring-cyan-200";

export type BusinessScopeCreateNotice = {
  readonly kind: "error";
  readonly text: string;
};

export const businessScopeCreateErrorNotice = (text: string): BusinessScopeCreateNotice => ({
  kind: "error",
  text,
});

export type BusinessScopeCreateSubmissionState = {
  readonly creating: boolean;
  readonly notice: BusinessScopeCreateNotice | null;
};

export const emptyBusinessScopeCreateSubmissionState = (): BusinessScopeCreateSubmissionState => ({
  creating: false,
  notice: null,
});

export type BusinessScopeCreateSubmissionAction =
  | { readonly type: "started" }
  | { readonly type: "succeeded" }
  | { readonly type: "failed"; readonly error: string };

export const nextBusinessScopeCreateSubmissionState = (
  _current: BusinessScopeCreateSubmissionState,
  action: BusinessScopeCreateSubmissionAction,
): BusinessScopeCreateSubmissionState => {
  switch (action.type) {
    case "started":
      return { creating: true, notice: null };
    case "succeeded":
      return { creating: false, notice: null };
    case "failed":
      return { creating: false, notice: businessScopeCreateErrorNotice(action.error) };
  }
};

const cleanRequired = (value: string): Result<string, BusinessScopeError> =>
  cleanRequiredText(value, { kind: "emptyScopeName" });

export type BusinessScopeCreateDraft<ScopeId extends string = string> = {
  readonly name: string;
  readonly kind: BusinessScopeKind;
  readonly description: string;
  readonly parentScopeId: ScopeId | "";
};

export const emptyBusinessScopeCreateFormState = <ScopeId extends string = string>(): BusinessScopeCreateDraft<ScopeId> => ({
  name: "",
  kind: "mine",
  description: "",
  parentScopeId: "",
});

export type BusinessScopeCreateFormAction<ScopeId extends string = string> =
  | { readonly type: "nameInput"; readonly value: string }
  | { readonly type: "kindInput"; readonly value: string }
  | { readonly type: "descriptionInput"; readonly value: string }
  | { readonly type: "parentScopeInput"; readonly value: ScopeId | "" };

export const nextBusinessScopeCreateFormState = <ScopeId extends string>(
  current: BusinessScopeCreateDraft<ScopeId>,
  action: BusinessScopeCreateFormAction<ScopeId>,
): BusinessScopeCreateDraft<ScopeId> => {
  switch (action.type) {
    case "nameInput":
      return { ...current, name: action.value };
    case "kindInput":
      return { ...current, kind: businessScopeKindFromInput(action.value, current.kind) };
    case "descriptionInput":
      return { ...current, description: action.value };
    case "parentScopeInput":
      return { ...current, parentScopeId: action.value };
  }
};

export const businessScopeCreateFormSubmittedState = <ScopeId extends string>(): BusinessScopeCreateDraft<ScopeId> =>
  emptyBusinessScopeCreateFormState<ScopeId>();

export type PreparedBusinessScopeCreateDraft<ScopeId extends string = string> = {
  readonly name: string;
  readonly kind: BusinessScopeKind;
  readonly description: string | null;
  readonly parentScopeId: ScopeId | null;
};

export const isBusinessScopeCreateDraftTouched = <ScopeId extends string>(
  draft: BusinessScopeCreateDraft<ScopeId>,
): boolean =>
  draft.name.trim() !== "" ||
  draft.description.trim() !== "" ||
  draft.parentScopeId !== "" ||
  draft.kind !== "mine";

export const prepareBusinessScopeCreateDraft = <ScopeId extends string>(
  draft: BusinessScopeCreateDraft<ScopeId>,
): Result<PreparedBusinessScopeCreateDraft<ScopeId>, BusinessScopeError> => {
  const name = cleanRequired(draft.name);
  if (!name.ok) return name;

  return ok({
    name: name.value,
    kind: draft.kind,
    description: cleanNullableText(draft.description),
    parentScopeId: draft.parentScopeId === "" ? null : draft.parentScopeId,
  });
};

export type BusinessScopeCreateFormInput<ScopeId extends string = string> = {
  readonly draft: BusinessScopeCreateDraft<ScopeId>;
  readonly hasCompany: boolean;
  readonly creating: boolean;
};

export type BusinessScopeCreateFormView<ScopeId extends string = string> = {
  readonly prepared: Result<PreparedBusinessScopeCreateDraft<ScopeId>, BusinessScopeError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly canSubmit: boolean;
  readonly buttonLabel: string;
};

export const businessScopeCreateFormView = <ScopeId extends string>(
  input: BusinessScopeCreateFormInput<ScopeId>,
): BusinessScopeCreateFormView<ScopeId> => {
  const prepared = prepareBusinessScopeCreateDraft(input.draft);
  const touched = isBusinessScopeCreateDraftTouched(input.draft);
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? businessScopeErrorMessage(prepared.error) : null,
    canSubmit: input.hasCompany && prepared.ok && !input.creating,
    buttonLabel: input.creating ? "Adding..." : "Add area",
  };
};

export type NewBusinessScope<CompanyId extends string, ScopeId extends string> = {
  readonly companyId: CompanyId;
  readonly parentScopeId: ScopeId | null;
  readonly name: string;
  readonly kind: BusinessScopeKind;
  readonly description: string | null;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly archivedAt: number | null;
};

export type CreateBusinessScopeInput<CompanyId extends string, ScopeId extends string> = {
  readonly companyId: CompanyId;
  readonly parentScopeId?: ScopeId | null;
  readonly name: string;
  readonly kind?: BusinessScopeKind;
  readonly description?: NullableText;
  readonly now: number;
};

export const prepareBusinessScopeCreate = <CompanyId extends string, ScopeId extends string>(
  input: CreateBusinessScopeInput<CompanyId, ScopeId>,
): Result<NewBusinessScope<CompanyId, ScopeId>, BusinessScopeError> => {
  const name = cleanRequired(input.name);
  if (!name.ok) return name;

  return ok({
    companyId: input.companyId,
    parentScopeId: input.parentScopeId ?? null,
    name: name.value,
    kind: input.kind ?? "mine",
    description: cleanNullableText(input.description),
    createdAt: input.now,
    updatedAt: input.now,
    archivedAt: null,
  });
};

export type BusinessScopePatch<ScopeId extends string> = {
  updatedAt: number;
  parentScopeId?: ScopeId | null;
  name?: string;
  kind?: BusinessScopeKind;
  description?: string | null;
  archivedAt?: number | null;
};

export type UpdateBusinessScopeInput<ScopeId extends string> = {
  readonly scopeId: ScopeId;
  readonly parentScopeId?: ScopeId | null;
  readonly name?: string;
  readonly kind?: BusinessScopeKind;
  readonly description?: NullableText;
  readonly archivedAt?: number | null;
  readonly now: number;
};

export const prepareBusinessScopeUpdate = <ScopeId extends string>(
  input: UpdateBusinessScopeInput<ScopeId>,
): Result<BusinessScopePatch<ScopeId>, BusinessScopeError> => {
  if (input.parentScopeId !== undefined) {
    const parentMove = validateBusinessScopeParentMove({
      scopes: [],
      scopeId: input.scopeId,
      candidateParentId: input.parentScopeId,
    });
    if (!parentMove.ok) return parentMove;
  }

  const patch: BusinessScopePatch<ScopeId> = { updatedAt: input.now };
  if (input.parentScopeId !== undefined) patch.parentScopeId = input.parentScopeId;
  if (input.name !== undefined) {
    const name = cleanRequired(input.name);
    if (!name.ok) return name;
    patch.name = name.value;
  }
  if (input.kind !== undefined) patch.kind = input.kind;
  if (input.description !== undefined) patch.description = cleanNullableText(input.description);
  if (input.archivedAt !== undefined) patch.archivedAt = input.archivedAt;
  return ok(patch);
};

export type BusinessScopeParentLink<ScopeId extends string> = {
  readonly _id: ScopeId;
  readonly parentScopeId: ScopeId | null;
};

export type BusinessScopeParentMoveInput<ScopeId extends string> = {
  readonly scopes: readonly BusinessScopeParentLink<ScopeId>[];
  readonly scopeId: ScopeId;
  readonly candidateParentId: ScopeId | null;
};

export const wouldCreateBusinessScopeCycle = <ScopeId extends string>(
  input: BusinessScopeParentMoveInput<ScopeId>,
): boolean => {
  if (input.candidateParentId === null) return false;

  const parentById = new Map(input.scopes.map((scope) => [scope._id, scope.parentScopeId]));
  const visited = new Set<ScopeId>();
  let current: ScopeId | null = input.candidateParentId;
  while (current !== null && !visited.has(current)) {
    if (current === input.scopeId) return true;
    visited.add(current);
    current = parentById.get(current) ?? null;
  }
  return false;
};

export const validateBusinessScopeParentMove = <ScopeId extends string>(
  input: BusinessScopeParentMoveInput<ScopeId>,
): Result<null, BusinessScopeError> => {
  if (input.candidateParentId === null) return ok(null);
  if (input.candidateParentId === input.scopeId) return err({ kind: "selfParentScope" });
  return wouldCreateBusinessScopeCycle(input)
    ? err({ kind: "descendantParentScope" })
    : ok(null);
};

